import { RouterProvider } from '@tanstack/react-router';
import { QueryProvider } from './providers/QueryProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { I18nProvider } from './providers/I18nProvider';
import { SessionProvider } from './providers/SessionProvider';
import { AppErrorBoundary } from './providers/AppErrorBoundary';
import { Toaster } from '@/shared/ui/toast';
import { router } from './router';

/**
 * Application root.
 *
 * Provider composition order (outermost → innermost):
 *   AppErrorBoundary              — catches any render error in the entire tree
 *   I18nProvider                  — language context
 *   ThemeProvider                 — light/dark/system theme
 *   QueryProvider                 — TanStack Query client
 *   SessionProvider               — session bootstrap (GET /me on mount)
 *   RouterProvider + Toaster      — TanStack Router tree, plus the single
 *                                   <Toaster /> mount so both the auth layout
 *                                   and the authenticated app layout can show
 *                                   toasts via shared/ui's toast() store.
 *

 * Theme and language are browser-local (persisted in localStorage) and are the
 * source of truth across the auth boundary — they are intentionally NOT
 * overridden from server preferences on login. The language chosen on the auth
 * page is instead pushed to the server (as the preferred locale) at
 * register/login so emails and notifications are localized correctly.
 */
export default function App() {
  return (
    <AppErrorBoundary>
      <I18nProvider>
        <ThemeProvider>
          <QueryProvider>
            <SessionProvider>
              <RouterProvider router={router} />
              <Toaster />
            </SessionProvider>
          </QueryProvider>
        </ThemeProvider>
      </I18nProvider>
    </AppErrorBoundary>
  );
}
